import React from 'react';

import { Avatar, AvatarProps, Box, CircularProgress, circularProgressClasses } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import type { FieldInputProps, FieldMetaState } from 'react-final-form';

import { filePickerFileTypes } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import { useDemoPrivacyBlurred } from 'src/hooks/demoPrivacyMode';
import { DroppedFile } from 'src/types/file';
import { SxProp } from 'src/types/mui';

type AvatarImageUploadFieldProps = {
  input: FieldInputProps<Nullable<DroppedFile>>;
  meta: FieldMetaState<any>;
  avatarProps?: Omit<AvatarProps, 'variant'>;
  variant?: AvatarProps['variant'];
  defaultImageUrl?: string;
  disabled?: boolean;
  loading?: boolean;
};

export const AvatarImageUploadField: React.FC<AvatarImageUploadFieldProps> = React.forwardRef(
  (
    {
      input,
      avatarProps,
      variant = 'circular',
      defaultImageUrl,
      disabled = false,
      loading = false,
    },
    ref,
  ) => {
    const { name, value: file, onChange: setFile } = input;

    const isBlurred = useDemoPrivacyBlurred();

    const onFilesDrop = React.useCallback(
      async (acceptedFiles: File[]) => {
        const [avatarFile] = acceptedFiles;
        if (!avatarFile) return;

        const newFile: DroppedFile = {
          id: window.crypto.randomUUID(),
          file: avatarFile,
          size: avatarFile.size,
        };

        setFile(newFile);
      },
      [setFile],
    );

    const imageUrl = React.useMemo(() => {
      if (!file?.file) return defaultImageUrl;
      return URL.createObjectURL(file.file);
    }, [file?.file, defaultImageUrl]);

    const acceptFiles = React.useMemo(() => {
      return { ...filePickerFileTypes.jpeg, ...filePickerFileTypes.png };
    }, []);

    const { getRootProps, getInputProps, open } = useDropzone({
      useFsAccessApi: false,
      maxFiles: 1,
      disabled,
      onDrop: onFilesDrop,
      accept: acceptFiles,
    });

    // pass ref if you want trigger file picker manually
    React.useImperativeHandle(ref, () => ({
      open,
    }));

    return (
      <Box sx={containerDynamicStyles(variant, loading, disabled)} {...getRootProps()}>
        <input name={name} {...getInputProps()} />

        <Box sx={backgroundStyles} />
        <Box sx={textStyles(disabled)}>
          <span>change</span>
        </Box>
        {loading && <CircularProgress />}
        <Avatar
          variant="square"
          {...avatarProps}
          src={isBlurred ? undefined : imageUrl}
          sx={isBlurred ? { filter: 'blur(4px)', userSelect: 'none' } : avatarProps?.sx}
        />
      </Box>
    );
  },
);

const backgroundStyles: SxProp = theme => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: theme.palette.grey[800],
  zIndex: 1,
  opacity: 0,
});

const textStyles = (disabled: boolean): SxProp => ({
  visibility: disabled ? 'hidden' : 'initial',
  position: 'absolute',
  opacity: 0,
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: '#dfa856',
  zIndex: 1,
  height: '28px',
  textTransform: 'uppercase',
  display: 'flex',
  justifyContent: 'center',
  span: {
    fontSize: '10px',
    marginTop: '3px',
    color: 'white',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
});

const containerDynamicStyles = (
  variant: AvatarProps['variant'],
  loading: boolean,
  disabled: boolean,
): SxProp => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  aspectRatio: '1 / 1',
  borderRadius: (variant === 'circular' && '50%') || (variant === 'rounded' && '5px') || 'none',
  overflow: 'hidden',
  cursor: loading || disabled ? 'node' : 'pointer',
  '& > :nth-child(1)': {
    opacity: loading || disabled ? 0.6 : 0,
  },
  '& > *': {
    transition: '0.2s ease',
  },
  '&:hover': {
    '& > :nth-child(3)': {
      opacity: 1,
    },
  },

  [`& > .${circularProgressClasses.root}`]: {
    position: 'absolute',
    zIndex: 1,
  },

  [`& > .${circularProgressClasses.colorPrimary}`]: {
    color: 'white',
  },
});
